import { useTranslation } from "react-i18next";
import type { Integrator } from "../../../../../../store/reseller-admin/integratorsStore";
import type { ThemeClasses } from "../types";
import { useIsUserDev } from "../../../../../../hooks/useIsUserDev";

interface IntegratorDetailsModalProps {
  integrator: Integrator;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
  onEdit: (integrator: Integrator) => void;
  onDelete: (integrator: Integrator) => void;
}

export default function IntegratorDetailsModal({
  integrator,
  themeClasses,
  isDarkMode,
  onClose,
  onEdit,
  onDelete,
}: IntegratorDetailsModalProps) {
  const { t } = useTranslation("resellerAdmin");
  const { isUserDev } = useIsUserDev();

  const protocolLabel = integrator.protocol
    ? t(`integrators.options.protocol.${integrator.protocol}`, {
        defaultValue: integrator.protocol,
      })
    : t("integrators.noData");
  const adapterLabel = integrator.adapter
    ? t(`integrators.options.adapter.${integrator.adapter}`, {
        defaultValue: integrator.adapter,
      })
    : t("integrators.noData");
  const authLabel = integrator.auth_strategy
    ? t(`integrators.options.authStrategy.${integrator.auth_strategy}`, {
        defaultValue: integrator.auth_strategy,
      })
    : t("integrators.noData");

  const Row = ({ label, value }: { label: string; value: string }) => (
    <div>
      <p className={`text-xs ${themeClasses.textSecondary}`}>{label}</p>
      <p className={`${themeClasses.text} break-words`}>{value}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div
        className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl ${themeClasses.bg} shadow-2xl`}
      >
        {/* Header */}
        <div
          className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${themeClasses.border} ${themeClasses.bg}`}
        >
          <h2 className={`text-lg font-bold ${themeClasses.text}`}>
            <i className="ri-plug-line mr-2"></i>
            {t("integrators.modal.detailsTitle")}
          </h2>
          <button
            aria-label={t("general.close", { ns: "common" })}
            onClick={onClose}
            className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText}`}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Title + status */}
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isDarkMode ? "bg-primary-500/15" : "bg-primary-50"
              }`}
            >
              <i className="ri-plug-line text-xl text-primary-500"></i>
            </div>
            <div>
              <h3 className={`text-lg font-bold ${themeClasses.text}`}>
                {integrator.name}
              </h3>
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                  integrator.active
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                }`}
              >
                {integrator.active
                  ? t("integrators.active")
                  : t("integrators.inactive")}
              </span>
            </div>
          </div>

          {/* Connection */}
          <div className={`p-4 rounded-xl ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
            <h4 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3`}>
              {t("integrators.sections.connection")}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Row label={t("integrators.fields.adapter")} value={adapterLabel} />
              <Row
                label={t("integrators.fields.authStrategy")}
                value={authLabel}
              />
              <Row
                label={t("integrators.fields.protocol")}
                value={protocolLabel}
              />
              <Row
                label={t("integrators.fields.defaultEndpoint")}
                value={integrator.default_endpoint || t("integrators.noData")}
              />
            </div>
          </div>

          {/* Credentials schema */}
          <div>
            <h4 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3`}>
              {t("integrators.sections.credentials")}
            </h4>
            {integrator.credentials_schema &&
            integrator.credentials_schema.length > 0 ? (
              <div className="space-y-2">
                {integrator.credentials_schema.map((cred, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}
                  >
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${themeClasses.text} truncate`}>
                        {cred.label || cred.field}
                      </p>
                      <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
                        {cred.field}
                      </p>
                    </div>
                    {cred.secret && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        <i className="ri-lock-line"></i>
                        {t("integrators.fields.credentialSecret")}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {t("integrators.noCredentials")}
              </p>
            )}
          </div>
        </div>

        {/* Footer actions */}
        {isUserDev && (
          <div
            className={`sticky bottom-0 flex items-center justify-end gap-3 p-6 border-t ${themeClasses.border} ${themeClasses.bg}`}
          >
            <button
              onClick={() => onDelete(integrator)}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all"
            >
              <i className="ri-delete-bin-line mr-1"></i>
              {t("integrators.actions.delete")}
            </button>
            <button
              onClick={() => onEdit(integrator)}
              className="px-6 py-2.5 rounded-xl text-sm font-medium bg-primary-500 text-gray-900 hover:bg-primary-600 transition-all shadow"
            >
              <i className="ri-edit-line mr-1"></i>
              {t("integrators.actions.edit")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
